import { useState, useEffect } from 'react';
import { 
  BuildingOffice2Icon,
  UserIcon,
  MapPinIcon,
  DocumentTextIcon,
  CreditCardIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import {
  Supplier,
  CreateSupplierDto,
  UpdateSupplierDto,
  SupplierType,
  SupplierStatus,
  PaymentTerms,
  getPaymentTermsLabel,
  formatSupplierType,
  formatSupplierStatus,
} from '../api/suppliers';
import TabbedFormActions, { isNonEmptyString } from './ui/TabbedFormActions';
import {
  BrandModal,
  BrandModalBody,
  BrandModalHeader,
} from './ui/BrandModal';
import AmountInput from './ui/AmountInput';

interface SupplierFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSupplierDto | UpdateSupplierDto) => Promise<void>;
  supplier?: Supplier | null;
  title: string;
}

const initialFormData: CreateSupplierDto = {
  name: '',
  code: '',
  type: SupplierType.MIXED,
  status: SupplierStatus.ACTIVE,
  contactPerson: '',
  email: '',
  phone: '',
  alternativePhone: '',
  address: '',
  city: '',
  province: '',
  postalCode: '',
  country: 'South Africa',
  website: '',
  taxNumber: '',
  registrationNumber: '',
  paymentTerms: PaymentTerms.NET_30,
  creditLimit: undefined,
  bankName: '',
  bankAccountNumber: '',
  bankBranchCode: '',
  description: '',
};

const SUPPLIER_TABS = ['basic', 'contact', 'address', 'business', 'banking', 'notes'] as const;

export default function SupplierForm({
  isOpen,
  onClose,
  onSubmit,
  supplier,
  title,
}: SupplierFormProps) {
  const [formData, setFormData] = useState<CreateSupplierDto>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState('basic');
  const canSubmit = isNonEmptyString(formData.name) && isNonEmptyString(formData.code);

  // Reset form when supplier changes or modal opens
  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name,
        code: supplier.code,
        type: supplier.type,
        status: supplier.status,
        contactPerson: supplier.contactPerson || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        alternativePhone: supplier.alternativePhone || '',
        address: supplier.address || '',
        city: supplier.city || '',
        province: supplier.province || '',
        postalCode: supplier.postalCode || '',
        country: supplier.country || 'South Africa',
        website: supplier.website || '',
        taxNumber: supplier.taxNumber || '',
        registrationNumber: supplier.registrationNumber || '',
        paymentTerms: supplier.paymentTerms,
        creditLimit: supplier.creditLimit,
        bankName: supplier.bankName || '',
        bankAccountNumber: supplier.bankAccountNumber || '',
        bankBranchCode: supplier.bankBranchCode || '',
        description: supplier.description || '',
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
    setActiveTab('basic'); // Reset to first tab
  }, [supplier, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Supplier name is required';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Supplier code is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Website must start with http:// or https://';
    }

    if (formData.creditLimit !== undefined && formData.creditLimit < 0) {
      newErrors.creditLimit = 'Credit limit cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Convert blank optional fields to undefined so backend format validators
      // (@IsEmail / @IsUrl) skip them instead of rejecting empty strings.
      const sanitized = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => [
          key,
          typeof value === 'string' && value.trim() === '' ? undefined : value,
        ]),
      ) as CreateSupplierDto;

      await onSubmit(sanitized);
      onClose();
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateSupplierDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader title={title} onClose={onClose} />
      <BrandModalBody>
                {/* Tabs */}
                <div className="border-b border-gray-200 mb-6">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      type="button"
                      onClick={() => setActiveTab('basic')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                        activeTab === 'basic'
                          ? 'border-cyan-500 text-cyan-600'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
                      }`}
                    >
                      <BuildingOffice2Icon className="h-4 w-4" />
                      <span>Basic Info</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('contact')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                        activeTab === 'contact'
                          ? 'border-cyan-500 text-cyan-600'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
                      }`}
                    >
                      <UserIcon className="h-4 w-4" />
                      <span>Contact</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('address')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                        activeTab === 'address'
                          ? 'border-cyan-500 text-cyan-600'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
                      }`}
                    >
                      <MapPinIcon className="h-4 w-4" />
                      <span>Address</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('business')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                        activeTab === 'business'
                          ? 'border-cyan-500 text-cyan-600'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
                      }`}
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      <span>Business</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('banking')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                        activeTab === 'banking'
                          ? 'border-cyan-500 text-cyan-600'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
                      }`}
                    >
                      <CreditCardIcon className="h-4 w-4" />
                      <span>Banking</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('notes')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                        activeTab === 'notes'
                          ? 'border-cyan-500 text-cyan-600'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
                      }`}
                    >
                      <InformationCircleIcon className="h-4 w-4" />
                      <span>Notes</span>
                    </button>
                  </nav>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information Tab */}
                  {activeTab === 'basic' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Supplier Name *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                              errors.name ? 'border-red-300' : ''
                            }`}
                            placeholder="Enter supplier name"
                          />
                          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Supplier Code *
                          </label>
                          <input
                            type="text"
                            value={formData.code}
                            onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                            className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                              errors.code ? 'border-red-300' : ''
                            }`}
                            placeholder="SUP001"
                          />
                          {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Supplier Type
                          </label>
                          <select
                            value={formData.type}
                            onChange={(e) => handleInputChange('type', e.target.value as SupplierType)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                          >
                            {Object.values(SupplierType).map((type) => (
                              <option key={type} value={type}>
                                {formatSupplierType(type)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Status
                          </label>
                          <select
                            value={formData.status}
                            onChange={(e) => handleInputChange('status', e.target.value as SupplierStatus)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                          >
                            {Object.values(SupplierStatus).map((status) => (
                              <option key={status} value={status}>
                                {formatSupplierStatus(status)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Contact Information Tab */}
                  {activeTab === 'contact' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Contact Person
                          </label>
                          <input
                            type="text"
                            value={formData.contactPerson}
                            onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="John Doe"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Email
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                              errors.email ? 'border-red-300' : ''
                            }`}
                            placeholder="contact@supplier.com"
                          />
                          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Phone
                          </label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="+27 11 123 4567"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Alternative Phone
                          </label>
                          <input
                            type="tel"
                            value={formData.alternativePhone}
                            onChange={(e) => handleInputChange('alternativePhone', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="+27 82 123 4567"
                          />
                        </div>

                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-slate-700">
                            Website
                          </label>
                          <input
                            type="url"
                            value={formData.website}
                            onChange={(e) => handleInputChange('website', e.target.value)}
                            className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                              errors.website ? 'border-red-300' : ''
                            }`}
                            placeholder="https://www.supplier.com"
                          />
                          {errors.website && <p className="mt-1 text-sm text-red-600">{errors.website}</p>}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Address Information Tab */}
                  {activeTab === 'address' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-slate-700">
                            Address
                          </label>
                          <textarea
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            rows={3}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="123 Main Street"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            City
                          </label>
                          <input
                            type="text"
                            value={formData.city}
                            onChange={(e) => handleInputChange('city', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="Johannesburg"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Province
                          </label>
                          <input
                            type="text"
                            value={formData.province}
                            onChange={(e) => handleInputChange('province', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="Gauteng"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Postal Code
                          </label>
                          <input
                            type="text"
                            value={formData.postalCode}
                            onChange={(e) => handleInputChange('postalCode', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="2000"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Country
                          </label>
                          <input
                            type="text"
                            value={formData.country}
                            onChange={(e) => handleInputChange('country', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="South Africa"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Business Information Tab */}
                  {activeTab === 'business' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Tax/VAT Number
                          </label>
                          <input
                            type="text"
                            value={formData.taxNumber}
                            onChange={(e) => handleInputChange('taxNumber', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="4123456789"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Registration Number
                          </label>
                          <input
                            type="text"
                            value={formData.registrationNumber}
                            onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="2021/123456/07"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Payment Terms
                          </label>
                          <select
                            value={formData.paymentTerms}
                            onChange={(e) => handleInputChange('paymentTerms', e.target.value as PaymentTerms)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                          >
                            {Object.values(PaymentTerms).map((terms) => (
                              <option key={terms} value={terms}>
                                {getPaymentTermsLabel(terms)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Credit Limit (R)
                          </label>
                          <AmountInput
                            value={formData.creditLimit}
                            onChange={(v) => handleInputChange('creditLimit', v)}
                            allowEmpty
                            min={0}
                            inputClassName={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                              errors.creditLimit ? 'border-red-300' : ''
                            }`}
                            placeholder="50000.00"
                          />
                          {errors.creditLimit && <p className="mt-1 text-sm text-red-600">{errors.creditLimit}</p>}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Banking Information Tab */}
                  {activeTab === 'banking' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-slate-700">
                            Bank Name
                          </label>
                          <input
                            type="text"
                            value={formData.bankName}
                            onChange={(e) => handleInputChange('bankName', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="Standard Bank"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Account Number
                          </label>
                          <input
                            type="text"
                            value={formData.bankAccountNumber}
                            onChange={(e) => handleInputChange('bankAccountNumber', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="123456789"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700">
                            Branch Code
                          </label>
                          <input
                            type="text"
                            value={formData.bankBranchCode}
                            onChange={(e) => handleInputChange('bankBranchCode', e.target.value)}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="051001"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes Tab */}
                  {activeTab === 'notes' && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700">
                          Description / Notes
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          rows={8}
                          className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                          placeholder="Additional notes about this supplier, special requirements, preferred contact methods, delivery instructions, etc..."
                        />
                      </div>
                    </div>
                  )}

                  {/* Form Actions */}
                  <TabbedFormActions
                    onCancel={onClose}
                    tabCount={SUPPLIER_TABS.length}
                    selectedTab={Math.max(0, SUPPLIER_TABS.indexOf(activeTab as typeof SUPPLIER_TABS[number]))}
                    onTabChange={(index) => setActiveTab(SUPPLIER_TABS[index] ?? 'basic')}
                    canSubmit={canSubmit}
                    submitLabel={supplier ? 'Update Supplier' : 'Create Supplier'}
                    loading={loading}
                  />
                </form>
      </BrandModalBody>
    </BrandModal>
  );
} 